import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand,
} from "@aws-sdk/client-cognito-identity";

import { fetchAuthSession } from "aws-amplify/auth";
// @ts-ignore
import aws4 from "aws4-tiny";
import { apiConfig } from "../../configs/api";
import {
  cognitoIdentityPoolId,
  userPoolId,
  region,
} from "../../configs/cognito";

export class AuthenticationService {
  private static instance: AuthenticationService;

  private constructor() {}

  public static getInstance(): AuthenticationService {
    if (!AuthenticationService.instance) {
      AuthenticationService.instance = new AuthenticationService();
    }
    return AuthenticationService.instance;
  }

  async getUser() {
    try {
      // fetchAuthSession automatically handles token refresh
      // It will refresh tokens if they're expired and valid refresh tokens exist
      const session = await fetchAuthSession({ forceRefresh: false });
      return session.tokens;
    } catch (error) {
      console.error("Error fetching user session:", error);
      return null;
    }
  }

  async getAuthToken() {
    try {
      // fetchAuthSession with forceRefresh: false will use cached tokens if valid
      // or automatically refresh if expired (invisible to user)
      const session = await fetchAuthSession({ forceRefresh: false });
      return session.tokens?.accessToken?.toString();
    } catch (error) {
      console.error("Error fetching access token:", error);
      return null;
    }
  }

  async getIdToken() {
    try {
      // fetchAuthSession handles token refresh automatically
      const session = await fetchAuthSession({ forceRefresh: false });
      return session.tokens?.idToken?.toString();
    } catch (error) {
      console.error("Error fetching ID token:", error);
      return null;
    }
  }

  // Method to force refresh tokens if needed
  async refreshTokens() {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      return session.tokens;
    } catch (error) {
      console.error("Error refreshing tokens:", error);
      return null;
    }
  }

  private async getAWSCredentials() {
    const userPoolProvider = `cognito-idp.${region}.amazonaws.com/${userPoolId}`;
    const idToken = await this.getIdToken();
    if (!idToken) {
      return {};
    }

    // Create a Cognito Identity Client
    const client = new CognitoIdentityClient({ region });

    // First, get the IdentityId using the logins map
    const getIdCommand = new GetIdCommand({
      IdentityPoolId: cognitoIdentityPoolId,
      Logins: {
        [userPoolProvider]: idToken,
      },
    });
    const identityResponse = await client.send(getIdCommand);

    // Then, get the temporary AWS credentials
    const getCredsCommand = new GetCredentialsForIdentityCommand({
      IdentityId: identityResponse.IdentityId,
      Logins: {
        [userPoolProvider]: idToken,
      },
    });
    const credsResponse = await client.send(getCredsCommand);
    return {
      credentials: credsResponse.Credentials,
      identityId: identityResponse.IdentityId,
    };
  }

  awsGraphqlFetch = async (
    input: string | URL | Request,
    init?: RequestInit | undefined
  ) => {
    const { host, graphqlPath, region } = apiConfig;

    const request = {
      host,
      method: "POST",
      path: graphqlPath,
      region,
      service: "execute-api",
      // NOTE: options.body is a json string
      body: init?.body,
      headers: {
        "X-WCA-IdToken": await this.getIdToken(),
        ...init?.headers,
      },
    };

    const { credentials, identityId } = await this.getAWSCredentials();
    if (!credentials || !identityId) {
      throw new Error("Failed to get AWS credentials");
    }

    const signedRequest = aws4.sign(request, {
      accessKeyId: credentials.AccessKeyId,
      secretAccessKey: credentials.SecretKey,
      sessionToken: credentials.SessionToken,
      expiration: credentials.Expiration,
      identityId,
    });
    if (signedRequest?.headers && signedRequest?.headers["Host"]) {
      delete signedRequest.headers["Host"];
    }
    if (signedRequest?.headers && signedRequest?.headers["Content-Length"]) {
      delete signedRequest.headers["Content-Length"];
    }
    return fetch(input, signedRequest as RequestInit);
  };
}
